/**
 * The Analyze window's repository part: scan a repository by its address (`repos.scan`), as a
 * folder would be, with its branch and one of its folders.
 *
 * How the core gets in is said here: this computer's own git and its logins (an SSH key in
 * ssh-agent, saved passwords) in the desktop app and the CLI; an access token kept by the core
 * for a host otherwise (a space, the browser build), or for a private repository over https.
 * Tokens are typed once and never shown again, only their end. Shown once a plugin reading
 * repositories (Git) is on; before that, a line leading to the Plugins window.
 */
import { createEffect, createSignal, For, on, Show, type JSX } from "solid-js";
import { useApp } from "../../app/context";
import { isReady } from "../../state/plugins";
import { ArmedButton } from "../../shared/ui";

/** The tokens kept, and the form adding one. */
function Tokens(): JSX.Element {
  const { analysis } = useApp();
  const [host, setHost] = createSignal("github.com");
  const [token, setToken] = createSignal("");
  /** Sends the typed token, then clears it from the page. */
  const keep = async () => {
    if (!host().trim() || !token().trim()) return;
    if (await analysis.setRepoToken(host(), token())) setToken("");
  };
  return (
    <details class="small" id="repo-tokens">
      <summary>Access tokens ({analysis.repoAccess()?.tokens.length ?? 0})</summary>
      <p class="small muted">
        For private repositories over https, and for GitHub's limits. Make a read-only token on the host (GitHub: a fine-grained token with "Contents: read"; Perforce: user:ticket, from p4 login -a -p). It is kept by {analysis.repoAccess()?.own_logins ? "this computer" : "the server, sealed"} and never shown again.
      </p>
      <ul>
        <For each={analysis.repoAccess()?.tokens ?? []}>
          {(t) => (
            <li class="row-inline" data-repo-token={t.host}>
              <span class="mono">{t.host}</span> <span class="muted mono">{t.hint}</span>
              <ArmedButton id={`repo-token-forget-${t.host}`} label="Forget" armedLabel="Click again to forget" onConfirm={() => void analysis.setRepoToken(t.host, "")} />
            </li>
          )}
        </For>
      </ul>
      <div class="row-inline">
        <input class="field mono" id="repo-token-host" placeholder="github.com" value={host()} onInput={(e) => setHost(e.currentTarget.value)} />
        <input
          class="field mono"
          id="repo-token-value"
          type="password"
          autocomplete="off"
          placeholder="token"
          value={token()}
          onInput={(e) => setToken(e.currentTarget.value)}
          onKeyDown={(e) => e.key === "Enter" && void keep()}
        />
        <button type="button" class="btn" id="repo-token-save" disabled={!host().trim() || !token().trim()} onClick={() => void keep()}>
          Keep
        </button>
      </div>
    </details>
  );
}

/** The Analyze window's repository part. */
export function RepoSection(): JSX.Element {
  const { analysis, plugins, wm } = useApp();
  // Which ways of fetching exist depends on the plugins turned on: ask again when they change.
  createEffect(on(() => plugins.list().filter(isReady).map((p) => p.id).join(","), () => void analysis.refreshAccess()));
  const ways = () => analysis.repoAccess()?.fetchers ?? [];
  return (
    <section class="grp" id="repo-section">
      <h3>From a repository</h3>
      <Show
        when={ways().length}
        fallback={
          <p class="small muted" id="repo-off">
            Turn on Git, Perforce or Plastic SCM to scan a repository by its address ·{" "}
            <button type="button" class="linklike link" id="repo-open-plugins" onClick={() => wm.open("plugins")}>
              Open Plugins
            </button>
          </p>
        }
      >
        <p class="small muted" id="repo-how">
          {analysis.repoAccess()?.own_logins
            ? "Fetched with the programs installed here (git, p4, cm) and their own logins (your SSH key, p4 login, the Plastic client), or a token below."
            : "Fetched by the server without its own logins: GitHub through its API, Perforce with a ticket kept below; private repositories need a token below."}{" "}
          Using: {ways().map((w) => w.name).join(", ")}.
        </p>
        <input
          class="field mono"
          id="repo-url"
          placeholder={analysis.repoAccess()?.own_logins ? "git@host:owner/name.git, perforce://host:1666//depot/project, plastic://host:8087/repo" : "https://github.com/owner/name, perforce://you@host:1666//depot/project"}
          value={analysis.repo().url}
          onInput={(e) => analysis.setRepo({ url: e.currentTarget.value })}
          onKeyDown={(e) => e.key === "Enter" && void analysis.scanRepo()}
        />
        <div class="row-inline">
          <input class="field mono" id="repo-branch" placeholder="branch, label or changelist (default)" value={analysis.repo().branch ?? ""} onInput={(e) => analysis.setRepo({ branch: e.currentTarget.value })} />
          <input class="field mono" id="repo-folder" placeholder="folder (all)" value={analysis.repo().folder ?? ""} onInput={(e) => analysis.setRepo({ folder: e.currentTarget.value })} />
        </div>
        <div class="btnrow">
          <button type="button" class="btn primary" id="repo-scan" disabled={analysis.running() || !analysis.repo().url.trim()} onClick={() => void analysis.scanRepo()}>
            {analysis.running() ? "Scanning…" : "Scan repository"}
          </button>
        </div>
        <Show when={analysis.error() && analysis.errorFrom() === "repo"}>
          <p class="small err" data-repo-error>
            {analysis.error()}
          </p>
        </Show>
        <Tokens />
      </Show>
    </section>
  );
}
